import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { Button } from "../../design/button";
import { Textarea } from "../../design/textarea";
import { Heading } from "../../design/heading";
import { Text } from "../../design/text";
import LoadingView from "../../components/LoadingView";

type SurveyType = "nps" | "feedback";

interface SurveyDoc {
  type: SurveyType;
  title: string;
  question: string;
  status?: string;
}

const SurveyPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();

  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState<SurveyDoc | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!surveyId) {
        setUnavailable(true);
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "surveys", surveyId));
        if (cancelled) return;

        if (!snap.exists()) {
          setUnavailable(true);
        } else {
          const data = snap.data() as SurveyDoc;
          if (data.status === "closed") {
            setUnavailable(true);
          } else {
            setSurvey(data);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setUnavailable(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [surveyId]);

  const handleSubmit = async () => {
    if (!surveyId || !survey || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const submitSurveyResponse = httpsCallable<
        { surveyId: string; score?: number; comment?: string },
        { success: boolean }
      >(functions, "submitSurveyResponse");

      await submitSurveyResponse({
        surveyId,
        score: survey.type === "nps" && score !== null ? score : undefined,
        comment: comment.trim() ? comment.trim() : undefined,
      });

      setSubmitted(true);
    } catch (err: any) {
      // The backend allows re-submit/overwrite, so an "already responded"
      // error is not a real failure - just show the thank-you state.
      const code: string | undefined = err?.code;
      const message: string = err?.message || "";
      if (
        code === "functions/already-exists" ||
        /already responded|already submitted/i.test(message)
      ) {
        setSubmitted(true);
      } else {
        setError(message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingView />;
  }

  const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-6 dark:bg-zinc-900">
      <div className="w-full max-w-lg rounded-xl border border-zinc-950/10 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-zinc-800">
        {children}
      </div>
    </div>
  );

  if (unavailable || !survey) {
    return (
      <Shell>
        <Heading>This survey is no longer available</Heading>
        <Text className="mt-3">
          The link may have expired or the survey has been closed. Thank you for
          your interest.
        </Text>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <Heading>Thank you!</Heading>
        <Text className="mt-3">
          Your response has been recorded. We appreciate your feedback.
        </Text>
      </Shell>
    );
  }

  const npsDisabled = survey.type === "nps" && score === null;

  return (
    <Shell>
      <Heading>{survey.title}</Heading>
      <Text className="mt-3">{survey.question}</Text>

      {survey.type === "nps" && (
        <div className="mt-6">
          <div className="grid grid-cols-11 gap-1.5">
            {Array.from({ length: 11 }, (_, i) => i).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setScore(value)}
                aria-pressed={score === value}
                className={
                  "flex h-10 items-center justify-center rounded-lg border text-sm font-semibold transition-colors " +
                  (score === value
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-zinc-950/10 bg-white text-zinc-950 hover:border-zinc-950/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:border-white/20")
                }
              >
                {value}
              </button>
            ))}
          </div>
          <div className="mt-2 flex justify-between">
            <Text className="text-xs">Not likely</Text>
            <Text className="text-xs">Extremely likely</Text>
          </div>
        </div>
      )}

      <div className="mt-6">
        <label className="mb-2 block text-sm/6 font-medium text-zinc-950 dark:text-white">
          {survey.type === "nps" ? "Comment (optional)" : "Your feedback"}
        </label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          placeholder="Share any additional thoughts..."
        />
      </div>

      {error && (
        <Text className="mt-4 text-red-600 dark:text-red-400">{error}</Text>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={submitting || npsDisabled}
        >
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </div>
    </Shell>
  );
};

export default SurveyPage;
